import React, { useState } from 'react';

import { Input } from '@eika/input';
import { Tabs } from '@eika/tabs';

import useTranslation from '../../../../hooks/useTranslation';
import { CrossBorderPaymentActionType } from '../form/actions';
import { BankValidationOptions } from '../form/state';
import useFormContext from '../form/useFormContext';
import { receiverError, validateBBANNumber } from '../form/validate';
import BicInput from './BicInput';
import NationalIdInput from './NationalIdInput';

interface Props {
    validationError?: receiverError;
    setValidationError: (errors: receiverError) => void;
}

const BankValidationFields: React.FC<Props> = ({ validationError, setValidationError }) => {
    const translated = useTranslation();
    const { state, handleChange } = useFormContext();
    const [bban, setBban] = useState(state.receiver?.accountNumber || '');
    const [bbanError, setBbanError] = useState<string | undefined>();
    const displayBankValidationOptions = state.receiver?.bankValidationOption || BankValidationOptions.BANKCODE;

    const defineBankValidationOption = (value: string) => {
        handleChange({
            type: CrossBorderPaymentActionType.SET_BANK_VALIDATION_OPTION,
            payload: BankValidationOptions[value as keyof typeof BankValidationOptions],
        });
        handleChange({
            type: CrossBorderPaymentActionType.RESET_BANK_VALIDATION,
        });
    };

    const validateBBAN = () => {
        const validation = validateBBANNumber(translated, bban, state);
        setBbanError(validation?.bban);

        if (!validation) {
            handleChange({ type: CrossBorderPaymentActionType.SET_RECEIVER_ACCOUNT_NUMBER, payload: bban });
        }
    };

    return (
        <>
            <Input
                className="cross-border-payment__field-top"
                label={translated.accountNumber}
                value={bban}
                onChange={(value) => {
                    const bban = value.target.value.toUpperCase().replace(/\s/g, '').trim();
                    setBban(bban);
                    setValidationError({ accountNumber: undefined });
                }}
                onBlur={validateBBAN}
                error={bbanError || validationError?.accountNumber}
            />
            {state.country?.ibanRequirements !== 'MANDATORY' && (
                <Tabs
                    id="bank-validation-option"
                    className="bank-validation-tabs"
                    defaultValue={displayBankValidationOptions}
                    onChangeValue={defineBankValidationOption}
                >
                    <Tabs.TabList>
                        <Tabs.TabButton value={BankValidationOptions.BANKCODE}>
                            {translated.crossBorderPayment.newReceiver.nationalIdLabel}
                        </Tabs.TabButton>
                        <Tabs.TabButton value={BankValidationOptions.BIC}>
                            {translated.crossBorderPayment.newReceiver.bicLabel}
                        </Tabs.TabButton>
                    </Tabs.TabList>
                    <Tabs.Tab value={BankValidationOptions.BANKCODE}>
                        <NationalIdInput setValidationError={setValidationError} validationError={validationError} />
                    </Tabs.Tab>
                    <Tabs.Tab value={BankValidationOptions.BIC}>
                        <BicInput setValidationError={setValidationError} validationError={validationError} />
                    </Tabs.Tab>
                </Tabs>
            )}
        </>
    );
};

export default BankValidationFields;
